// SPIRUVA Product Catalogue — pre-launch, July 2027 commercial readiness.
// NO pricing. CTAs are per-grade and feed into reserve-allocation / technical-review flows.

export const PRODUCTS = [
  {
    id: "e18",
    name: "Phycocyanin E18",
    grade: "E18",
    tier: "Food Grade",
    purityRatio: "A620/A280: aligned to E18 food-grade application review",
    purityLevel: 0.7,
    forms: ["Powder", "Liquid Concentrate"],
    applications: ["Food coloring", "Beverages", "Confectionery", "Ice cream", "Dairy"],
    certifications: ["FSSAI", "HACCP", "Halal"],
    documentation: "TDS · COA framework · launch-stage technical review",
    summary: "A formulation-ready natural blue grade designed for food, beverage, confectionery, and dairy applications where color performance, sourcing confidence, and early commercial planning matter most.",
    ctaLabel: "Reserve Allocation",
    ctaIntent: "reserve",
  },
  {
    id: "e25",
    name: "Phycocyanin E25",
    grade: "E25",
    tier: "Cosmetic / Nutraceutical Grade",
    purityRatio: "A620/A280: aligned to E25 cosmetic and nutraceutical evaluation",
    purityLevel: 1.5,
    forms: ["Freeze-dried powder", "Liquid"],
    applications: ["Skincare serums", "Supplements", "Functional beverages", "Nutraceuticals"],
    certifications: ["ISO 22000", "Organic", "HACCP"],
    documentation: "TDS · COA framework · technical review pathway",
    summary: "A higher-purity grade for premium skincare, nutraceutical formulation, and brands seeking stronger technical positioning as they prepare post-launch product programs.",
    ctaLabel: "Request Technical Review",
    ctaIntent: "technical",
  },
  {
    id: "e30",
    name: "Phycocyanin E30",
    grade: "E30",
    tier: "Pharma / Research Grade",
    purityRatio: "A620/A280: aligned to E30 high-spec evaluation",
    purityLevel: 3.0,
    forms: ["Freeze-dried powder"],
    applications: ["Pharma R&D", "Clinical nutrition", "Advanced bioactive formulations", "Research workflows"],
    certifications: ["WHO-GMP (in progress)", "GLP"],
    documentation: "TDS · release documentation framework · traceability-first review",
    summary: "A high-purity grade intended for advanced clinical, research, and formulation teams that require stronger purity positioning, deeper traceability expectations, and technical dialogue before launch-stage supply planning.",
    ctaLabel: "Request Technical Review",
    ctaIntent: "technical",
  },
  {
    id: "e40",
    name: "Phycocyanin E40",
    grade: "E40",
    tier: "Advanced Technical Grade",
    purityRatio: "A620/A280: aligned to E40 advanced technical review",
    purityLevel: 4.0,
    forms: ["High-purity powder", "Custom evaluation format subject to review"],
    applications: ["Diagnostics", "Fluorescence-linked systems", "High-spec pharma workflows", "Institutional R&D"],
    certifications: ["Technical dossier pathway"],
    documentation: "Technical dossier pathway · advanced specification review · application-fit assessment",
    summary: "Our highest-purity phycocyanin pathway, positioned for advanced pharma, diagnostics, fluorescence-linked workflows, and institutional buyers evaluating elite-spec natural blue biomaterials.",
    ctaLabel: "Discuss Technical Fit",
    ctaIntent: "e40",
    hero: true,
  },
  {
    id: "organic-spirulina",
    name: "Organic Spirulina Powder",
    grade: "Organic",
    tier: "Whole Biomass",
    purityRatio: "Protein 60–70% dry weight (typical evaluation profile)",
    purityLevel: 1.0,
    forms: ["Fine powder"],
    applications: ["Dietary supplements", "Sports nutrition", "Protein fortification", "Functional foods"],
    certifications: ["USDA Organic (target)", "NPOP", "Kosher", "Halal"],
    documentation: "TDS · COA framework · organic-chain documentation pathway",
    summary: "Sun-cultivated Arthrospira platensis grown in the naturally alkaline waters of India. Designed for distributors and brand owners planning organic-supply programs ahead of July 2027 readiness.",
    ctaLabel: "Reserve Allocation",
    ctaIntent: "reserve",
  },
];

export const MARKETS = [
  {
    code: "EU",
    flag: "🇪🇺",
    name: "European Union",
    headline: "Post-FTA zero-duty corridor · phycocyanin authorised 2024",
    size: "€240M",
    sizeLabel: "EU phycocyanin TAM",
    tariff: "0% (Indo-EU FTA)",
    certs: ["EU Organic", "ISO 22000", "HACCP"],
    buyers: "Cosmetics OEMs, beverage brands, supplement co-packers",
  },
  {
    code: "US",
    flag: "🇺🇸",
    name: "United States",
    headline: "FDA Synthetic Dye Phase-Out · natural-blue acceleration",
    size: "$1.2B",
    sizeLabel: "natural colors TAM by 2027",
    tariff: "0% GSP (negotiation)",
    certs: ["USDA Organic", "FDA GRAS", "Non-GMO"],
    buyers: "Beverage majors, nutraceutical OEMs, clinical nutrition",
  },
  {
    code: "JP",
    flag: "🇯🇵",
    name: "Japan",
    headline: "India-Japan CEPA · premium organic positioning",
    size: "$30–55/kg",
    sizeLabel: "Premium pricing precedent",
    tariff: "0% (CEPA)",
    certs: ["JAS Organic", "Halal", "Kosher"],
    buyers: "Health-tea brands, K-J beauty exporters, supplement OEMs",
  },
  {
    code: "AE",
    flag: "🇦🇪",
    name: "UAE & GCC",
    headline: "India-UAE CEPA · Halal-certified MENA hub",
    size: "$180M",
    sizeLabel: "GCC nutraceutical TAM",
    tariff: "0% (CEPA)",
    certs: ["Halal (UAE)", "ESMA", "ISO 22000"],
    buyers: "Functional beverage brands, halal supplement makers",
  },
  {
    code: "AU",
    flag: "🇦🇺",
    name: "Australia",
    headline: "India-Australia ECTA · clean-label growth corridor",
    size: "$420M",
    sizeLabel: "AU supplements TAM",
    tariff: "0% (ECTA)",
    certs: ["TGA Listed", "ACO Organic"],
    buyers: "Sports nutrition, clean-label beverage brands",
  },
  {
    code: "KR",
    flag: "🇰🇷",
    name: "South Korea",
    headline: "K-Beauty phycocyanin demand window opening",
    size: "$95M",
    sizeLabel: "K-cosmetic blue actives",
    tariff: "Preferential (CEPA)",
    certs: ["KFDA", "Halal", "Vegan"],
    buyers: "K-Beauty brands, functional food exporters",
  },
];

export const CERTIFICATIONS = [
  { name: "FSSAI Central Licence", status: "Target", category: "Food Safety" },
  { name: "APEDA Registration", status: "Target", category: "Export" },
  { name: "IEC (DGFT)", status: "Target", category: "Export" },
  { name: "USDA Organic (NPOP)", status: "Target", category: "Organic" },
  { name: "EU Organic", status: "Target", category: "Organic" },
  { name: "HACCP", status: "Target", category: "Quality" },
  { name: "ISO 22000:2018", status: "Target", category: "Quality" },
  { name: "Halal (JUH-India)", status: "Target", category: "Religious" },
  { name: "Kosher", status: "Target", category: "Religious" },
  { name: "Non-GMO Verified", status: "Target", category: "Identity" },
  { name: "WHO-GMP (E30/E40 line)", status: "Target", category: "Pharma" },
  { name: "NABL Lab Accreditation", status: "Target", category: "Testing" },
];
